import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Avatar from "../../../interfaces/avatar/Avatar";
import { toast, Id } from "react-toastify";
import { useAuth } from "../../../firebase/AuthProvider";
import { selectedConversation$, floatingChatVisible$, resourceComposeRequest$, ResourceComposeRequest, resourceComposeScope, chatRequestActor } from "../../../services/Subjects/ChatSubjects";
import { currentConversationId$, UpdateSelectedRegionIdFromHeaderSubject, UpdateSelectedWaiverIdFromHeaderSubject } from "../../../services/Subjects/userSubject";
import { Conversation, ChatActivity, ChatReadState, getRecentChatActivity, getMyConversations, Message, markChatActivitiesRead, markChatActivityForMessageRead, markConversationAsRead } from "../../../services/chatService/ChatService";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import { getAvatarsFromStorage } from "../../../services/fileServices/FileService";
import { TeamUser } from "../../../interfaces/team/TeamUser";
import { getTeamBySupervisor } from "../../../services/userServices/TeamService";
import { selectedUserForTask$ } from "../../../services/Subjects/userSubject";
import "./Headercomponent.css";
import ChatPage from "../../components/realtimeChatComponents/ChatPage";
import { ConversationListRef } from "../../components/realtimeChatComponents/ConversationListComponent";
import PopupReactionActivity, { getConversationReactionActivityIds, getPopupExpandTarget, getReactionBadges, isUnreadReactionActivity, persistActivitiesRead } from "../../components/realtimeChatComponents/PopupReactionActivity";

import NotificationPanel from "../../components/notifications/NotificationPanel";
import { useNotificationState } from "../../../contexts/NotificationStateContext";
import { isFileViewerRoute, usePageMetadataTitle } from "../../../hooks/usePageMetadataTitle";
import useUpdateTitle from "../../../hooks/useUpdateTitle";
import { conversationRead$ } from "../../../hooks/globalChatHooks/useGlobalChat";
import { useChatReadCountReconciliation } from "../../../hooks/globalChatHooks/useChatReadCountReconciliation";
import MilestoneAvatar from "../../ui/Avatar/MilestoneAvatar";
import MobileMenu from "./MobileMenu";
import GlobalSearch from "../../components/search/GlobalSearch";
import AddNewBtn from "./AddNewBtn";
import { getAllRegions } from "../../../services/userServices/UserInfoService";
import Region from "../../../interfaces/identity/Region";
import Waiver from "../../../interfaces/participant/Waiver";
import { getWaivers } from "../../../services/participantServices/ParticipantService";
import { ROLE_PRIORITY, ROLES } from "../../../Data/SD";
import { HEADER_ROLE_MAP } from "../../components/dashboard/OwnerDashboard/OwnerParticipantsElement";
import { Bell, LucideMessageCircleMore, ChevronDown, User, Settings, LogOut, Menu, Plus, Maximize2, Minus, CreditCard, Users, BadgeQuestionMark, Moon, Sun } from "lucide-react";
import HeaderCalendarDropdown from "./features/HeaderCalendarDropdown";
import { ChatMessageToast, UnreadMessagesToast } from "../../components/notifications/ChatMessageToast";

import Tooltip from "../../ui/Tooltip/Tooltip";
import BillingAlertBanner from "./BillingAlertBanner";
import { hasPolicyPermissionV2 } from "../../../utilities/common/policyFuntions";
import { POLICYPERMISSIONS } from "../../../Data/PolicyPermissions";

const getToastSessionKey = (userId: string) => `unreadToastShown_${userId}`;
const markToastAsShown = (userId: string) => sessionStorage.setItem(getToastSessionKey(userId), "true");

interface HeaderComponentProps {
  handleShowSidebar: () => void;
  canUseDarkMode: boolean;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
}

// Helpers
const toTimestamp = (d: string | number | Date | null | undefined): number | null => {
  if (!d && d !== 0) return null;
  if (d instanceof Date) return d.getTime();
  if (typeof d === "number") return Number.isFinite(d) ? d : null;
  if (typeof d === "string") {
    const t = Date.parse(d);
    return Number.isFinite(t) ? t : null;
  }
  return null;
};

const normalizeMessage = (m: Message): Message & { _ts?: number } => {
  const ts = toTimestamp((m as any).createDate ?? (m as any).createdAt);
  return { ...m, _ts: ts ?? undefined };
};

const isConversationUnread = (conv: Conversation, currentUserId: string): boolean => {
  const lastMsg = conv.lastMessage as any | undefined;
  if (!lastMsg || (typeof lastMsg === "object" && Object.keys(lastMsg).length === 0)) return false;

  const normalizedMsg = normalizeMessage(lastMsg as Message);
  const lastMsgTs = normalizedMsg._ts ?? toTimestamp((lastMsg as any).createDate ?? (lastMsg as any).createdAt);
  const dateSaysRead = !!lastMsgTs && !!conv.lastReadAt && new Date(conv.lastReadAt).getTime() >= lastMsgTs;
  const authoredByMe = normalizedMsg.author?.id === currentUserId;

  const read = (typeof conv.unreadCount === "number" ? conv.unreadCount === 0 : !!lastMsg && !!conv.lastReadAt && dateSaysRead) || authoredByMe;
  return !read;
};

function splitCamelCase(str: string) { return str.replace(/([a-z])([A-Z])/g, "$1 $2"); }
function toTitleCase(str: string) { return splitCamelCase(str).replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()); }

const getPageTitle = (pathname: string): string => {
  if (isFileViewerRoute(pathname)) return "File viewer";
  if (pathname === "/" || pathname === "/login") return "Login";
  if (pathname === "/chat" || pathname.startsWith("/chat/")) { return "Chat"; }
  if (pathname.startsWith("/profile/user")) return "User Profile";
  if (pathname.endsWith("/dashboard/owner") || pathname.endsWith("/dashboard") || pathname.endsWith("/home")) return "Dashboard";
  if (pathname.endsWith("/participant-snapshot")) return "Participant Files";
  if (pathname.startsWith("/tasks") || pathname.startsWith("/task")) return "Tasks";
  if (pathname.includes("/participants")) return "Participants";
  if (pathname.startsWith("/superadmin/workflow")) return "Super Admin Workflows";
  if (pathname.startsWith("/superadmin/projections")) return "Super Admin Recurring Projections";
  if (pathname.startsWith("/dashboard/superadmin")) return "Super Admin Dashboard";
  if (pathname.startsWith("/drive") || pathname.includes("/drive/caseFileDrive")) return "Drive";
  if (pathname.startsWith("/dashboard/owner/team")) return "Team";
  if (pathname.startsWith("/admin-requests")) return "Admin Requests";
  if (pathname.startsWith("/issues") || pathname.startsWith("/issue")) return "Issues";
  if (pathname.startsWith("/sprints")) return "Sprints";
  if (pathname.startsWith("/faqs")) return "FAQs";
  if (pathname.startsWith("/admin/faqs")) return "FAQs";

  const fullTitleOverrides: Record<string, string> = {
    "/drive/employee-files": "Employee Files",
    "/drive/caseFileDrive": "Participant Files",
    "/drive/personal": "Personal Files",
    "/home": "Home",
  };

  if (fullTitleOverrides[pathname]) return fullTitleOverrides[pathname];

  const titleOverrides: Record<string, string> = {
    "/terms-of-service": "Terms of Service",
    "/privacy-policy": "Privacy Policy",
    "/legal/sign/tos": "Terms of Service",
    "/legal/sign/data-agreement": "Data Processing Agreement",
    "/legal/sign/security-compliance-terms": "Security & Compliance Terms",
    "/legal/sign/electronic-signature-consent": "Electronic Signature Consent",
  };

  const parts = pathname.split("/").filter(Boolean);
  let formattedParts = parts.map(toTitleCase);
  if (formattedParts[0] === "Drive" && formattedParts[1]?.endsWith("Drive")) {
    formattedParts[1] = formattedParts[1].replace(/Drive$/, "").trim();
  }

  const rawTitle = titleOverrides[pathname] ?? (formattedParts.length ? formattedParts.join(" ") : "Home");
  return rawTitle.replace(/^\(\d+\)\s*/, "");
};

export default function HeaderComponent({ handleShowSidebar, canUseDarkMode, isDarkMode, onToggleDarkMode }: HeaderComponentProps) {
  const location = useLocation();
  const metadataTitle = usePageMetadataTitle(location.pathname);
  const navigate = useNavigate();
  const [showSideChat, setShowSideChat] = useState(false);
  useLayoutEffect(() => {
    floatingChatVisible$.next(showSideChat && !location.pathname.startsWith('/chat'));
  }, [showSideChat, location.pathname]);
  useLayoutEffect(() => () => { floatingChatVisible$.next(false); }, []);
  const [resourceComposeRequest, setResourceComposeRequest] = useState<ResourceComposeRequest | null>(null);
  const [popupChatTarget, setPopupChatTarget] = useState<{
    conversationId: number;
    messageId: number;
    threadRootMessageId?: number;
  } | null>(null);
  const showSideChatRef = useRef<boolean>(false);
  const resourceShareTrigger = useRef<HTMLElement | null>(null);

  const handleCloseSideChat = () => {
    const trigger = resourceShareTrigger.current;
    resourceShareTrigger.current = null;
    if (trigger?.isConnected) window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
    setResourceComposeRequest(null);
    setShowSideChat(false);
    showSideChatRef.current = false;
  };

  const handleCloseChat = () => {
    handleCloseSideChat();
  };


  const handleShowSideChat = useCallback(() => {
    if (location.pathname.startsWith("/chat")) return;
    setShowSideChat(true);
    showSideChatRef.current = true;
  }, [location.pathname]);

  const { currentUser, currentRoles, currentUserInfo, currentOrganizationRoles, userAddedInRolePolicy, currentOrganizationRolePolicies, impersonatedUserId, setImpersonation } = useAuth();
  const resourceShareScope = resourceComposeScope(currentUser?.uid ?? '', currentUserInfo?.id ?? '', currentUserInfo?.organization?.id,
    (currentRoles ?? []).map(role => role.name).sort().join('|'));
  useEffect(() => {
    setResourceComposeRequest(null);
    const subscription = resourceComposeRequest$.subscribe(request => {
      if (location.pathname.startsWith("/chat") || impersonatedUserId || chatRequestActor() || !request || request.actorId || resourceComposeScope(request.userId, request.employeeId, request.organizationId, request.roles, request.actorId ?? '') !== resourceShareScope) return;
      resourceShareTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setResourceComposeRequest(request); setPopupChatTarget(null);
      setShowSideChat(true); showSideChatRef.current = true;
    });
    return () => subscription.unsubscribe();
  }, [resourceShareScope, impersonatedUserId, location.pathname]);
  const { notifications, hasUnread } = useNotificationState();
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  useUpdateTitle(totalUnreadCount, location.pathname);

  // Add this state right below your other UI states:
  const [targetUserId, setTargetUserId] = useState("");
  
  // Strict check for the legacy EmployeeRole
  const isSuperAdmin = currentRoles?.some((role) => role.name.toUpperCase() === "SUPER ADMIN");
  
  // Modern UI States
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isWaiverOpen, setIsWaiverOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);

  const [, setTaskId] = useState<string>("");
  const [currentAvatar, setCurrentAvatar] = useState<Avatar | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  useChatReadCountReconciliation(conversations, setConversations);
  const { connection, connectionStatus, connectionVersion } = useChatConnection();
  const previousChatUserIdRef = useRef<string | null>(null);
  const [users, setUsers] = useState<TeamUser[] | undefined>();
  const [selectedUsers, setSelectedUsers] = useState<TeamUser[]>(selectedUserForTask$.getValue());
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [chatActivities, setChatActivities] = useState<ChatActivity[]>([]);
  const [showChatHead, setShowChatHead] = useState<boolean>(true);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<number>(0);
  const [chatIconFeedback, setChatIconFeedback] = useState(false);
  const chatIconFeedbackTimerRef = useRef<number>(0);
  const [region, setRegion] = useState<Region[]>([]);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [selectedWaiver, setSelectedWaiver] = useState<Waiver | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);

  useEffect(() => () => window.clearTimeout(chatIconFeedbackTimerRef.current), []);

  const handleChatIconClick = () => {
    if (!location.pathname.startsWith("/chat")) {
      if (showSideChatRef.current) handleCloseSideChat();
      else handleShowSideChat();
      return;
    }

    window.clearTimeout(chatIconFeedbackTimerRef.current);
    setChatIconFeedback(true);
    chatIconFeedbackTimerRef.current = window.setTimeout(() => setChatIconFeedback(false), 420);

    const currentRouteConversationId = Number(location.pathname.match(/^\/chat\/(\d+)/)?.[1]) || 0;
    const destinationConversationId = currentRouteConversationId || selectedConversationId;
    if (new URLSearchParams(location.search).get("view")) {
      navigate(destinationConversationId > 0 ? `/chat/${destinationConversationId}` : "/chat");
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.querySelector<HTMLElement>("[data-chat-primary-region]")?.focus({ preventScroll: true });
      });
    });
  };

  // Refs for closing dropdowns
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const waiverRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const headerRightIconRef = useRef<HTMLUListElement>(null);

  const toastIdMap = useRef<Map<number, Id>>(new Map());
  const conversationListRef = useRef<ConversationListRef>(null);
  const addConversationButtonRef = useRef<HTMLButtonElement>(null);
  const unreadConversationsToastId = useRef<Id | null>(null);

  const isOwner = currentRoles?.some((role) => role.name === ROLES.OWNER) || currentOrganizationRoles?.some((role) => role.systemKey === "ORG_OWNER");





  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!headerRightIconRef.current || isMobile) {
      setShowOverflowMenu(false);
      return;
    }
    const checkOverflow = () => {
      const container = headerRightIconRef.current;
      if (!container) return;
      const isOverflowing = container.scrollWidth > container.clientWidth;
      const parent = container.parentElement;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const availableWidth = parentRect.width - parentRect.width * 0.1;
        if (containerRect.width > availableWidth || isOverflowing) {
          setShowOverflowMenu(true);
          return;
        }
      }
      setShowOverflowMenu(isOverflowing);
    };

    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);
    const resizeObserver = new ResizeObserver(() => setTimeout(checkOverflow, 50));
    if (headerRightIconRef.current) resizeObserver.observe(headerRightIconRef.current);
    if (headerRightIconRef.current?.parentElement) resizeObserver.observe(headerRightIconRef.current.parentElement);

    return () => { clearTimeout(timeoutId); window.removeEventListener("resize", checkOverflow); resizeObserver.disconnect(); };
  }, [isMobile, waivers, region, selectedWaiver, selectedRegion]);

  useEffect(() => { showSideChatRef.current = showSideChat; }, [showSideChat]);

  useEffect(() => {
    if (!location.pathname.startsWith("/chat")) return;
    setShowSideChat(false);
    showSideChatRef.current = false;
  }, [location.pathname]);

  // Never carry conversation/unread state across authenticated identities.
  useEffect(() => {
    const nextUserId = currentUser?.uid ?? null;
    const previousUserId = previousChatUserIdRef.current;

    if (previousUserId !== nextUserId) {
      console.debug("[CHAT-UNREAD] Auth identity changed; clearing header chat state", {
        previousUserId,
        nextUserId,
      });

      setConversations([]);
      setHasUnreadMessages(false);
      setTotalUnreadCount(0);
      toastIdMap.current.clear();
      unreadConversationsToastId.current = null;
      currentConversationId$.next(0);
      selectedConversation$.next(0);
      setSelectedConversationId(0);

      previousChatUserIdRef.current = nextUserId;
    }
  }, [currentUser]);

  useEffect(() => {
  if (!currentUser) {
    setHasUnreadMessages(false);
    return;
  }

  setHasUnreadMessages(
    conversations.some((conversation) =>
      isConversationUnread(conversation, currentUser.uid)
    )
  );
}, [conversations, currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unreadNotifCount = notifications.filter((n) => !n.read).length;
    const unreadMsgCount = conversations.filter((c) => isConversationUnread(c, currentUser.uid)).length;
    setTotalUnreadCount(unreadNotifCount + unreadMsgCount);
  }, [notifications, conversations, currentUser]);

  useEffect(() => {
    if (showSideChat && currentUser) {
      if (unreadConversationsToastId.current) {
        toast.dismiss(unreadConversationsToastId.current);
        unreadConversationsToastId.current = null;
      }
      markToastAsShown(currentUser.uid);
    }
  }, [showSideChat, currentUser]);

  useEffect(() => {
const hasShownToastForSession = (userId: string): boolean => sessionStorage.getItem(getToastSessionKey(userId)) === "true";

    if (!currentUser) return;
    const unreadConvCount = conversations.filter((c) => isConversationUnread(c, currentUser.uid)).length;
    if (unreadConvCount > 0 && !hasShownToastForSession(currentUser.uid) && !unreadConversationsToastId.current && !showSideChat) {
      const toastId = toast(
        <UnreadMessagesToast
          count={unreadConvCount}
          onViewMessages={handleShowSideChat}
        />, {
        toastId: `unread-conversations:${currentUser.uid}`,
        closeOnClick: false,
        closeButton: false,
        hideProgressBar: false,
        progressClassName: "waivera-chat-toast-progress",
        autoClose: 5000,
        className: "waivera-toast waivera-chat-toast",
        onClose: () => { unreadConversationsToastId.current = null; markToastAsShown(currentUser.uid); },
      });
      unreadConversationsToastId.current = toastId;
      markToastAsShown(currentUser.uid);
    }
  }, [conversations, showSideChat, currentUser, handleShowSideChat]);

  useEffect(() => {
    const subscription = conversationRead$.subscribe((readConversationId) => {
      setConversations((prev) => prev.map((convo) => convo.id === readConversationId ? { ...convo, unreadCount: 0 } : convo));
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    setSelectedConversationId(currentConversationId$.getValue());
    const subscription = currentConversationId$.subscribe(setSelectedConversationId);
    return () => subscription.unsubscribe();
  }, []);

  const refreshChatActivity = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      setChatActivities(await getRecentChatActivity(token));
    } catch (error) {
      console.error("Failed to load recent chat activity", error);
    }
  }, [currentUser]);

  useEffect(() => {
    void refreshChatActivity();
  }, [refreshChatActivity, connectionVersion]);

  useEffect(() => {
    if (!connection) return;

    const handleActivity = (activity: ChatActivity) => {
      setChatActivities(previous => [activity, ...previous.filter(item => item.id !== activity.id)]);
    };
    const handleActivityRemoved = (activityId: number) => {
      setChatActivities(previous => previous.filter(item => item.id !== activityId));
    };
    const handleReadState = (state: ChatReadState) => {
      const readIds = new Set(state.readActivityIds ?? []);
      setChatActivities(previous => previous.map(activity => readIds.has(activity.id) ? { ...activity, read: true } : activity));
      const toastId = toastIdMap.current.get(state.conversationId);
      if (toastId) {
        toast.dismiss(toastId);
        toastIdMap.current.delete(state.conversationId);
      }
      window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: state }));
    };

    connection.on("ReceiveChatActivity", handleActivity);
    connection.on("RemoveChatActivity", handleActivityRemoved);
    connection.on("ReceiveChatReadState", handleReadState);
    return () => {
      connection.off("ReceiveChatActivity", handleActivity);
      connection.off("RemoveChatActivity", handleActivityRemoved);
      connection.off("ReceiveChatReadState", handleReadState);
    };
  }, [connection]);

  useEffect(() => {
    const handleActivityRead = (event: Event) => {
      const detail = (event as CustomEvent<{ activityId?: number; messageId?: number; conversationId?: number; threadRootMessageId?: number | null; readActivityIds?: number[]; all?: boolean }>).detail;
      const readIds = new Set(detail?.readActivityIds ?? []);
      setChatActivities(previous => previous.map(activity =>
        detail?.all || readIds.has(activity.id) || activity.id === detail?.activityId || activity.messageId === detail?.messageId
          ? { ...activity, read: true }
          : activity
      ));
      if (detail?.conversationId) {
        const toastId = toastIdMap.current.get(detail.conversationId);
        if (toastId) {
          toast.dismiss(toastId);
          toastIdMap.current.delete(detail.conversationId);
        }
      }
    };
    window.addEventListener("chat-activity-read", handleActivityRead);
    return () => window.removeEventListener("chat-activity-read", handleActivityRead);
  }, []);

  // Popup Messages: surface unread reactions in the list instead of only on the
  // full Chat Activity view (WAIV-2921).
  const unreadReactionActivities = useMemo(() => chatActivities.filter(isUnreadReactionActivity), [chatActivities]);
  const reactionBadges = useMemo(() => getReactionBadges(chatActivities), [chatActivities]);

  const markActivitiesRead = useCallback(async (activityIds: number[]) => {
    if (!currentUser || activityIds.length === 0) return;
    const readIds = new Set(activityIds);
    // Optimistic for the header dot and popup only; other views hear about it after the save succeeds.
    setChatActivities(previous => previous.map(activity => readIds.has(activity.id) ? { ...activity, read: true } : activity));
    const saved = await persistActivitiesRead(activityIds, async ids => markChatActivitiesRead(await currentUser.getIdToken(), ids));
    if (!saved) void refreshChatActivity();
  }, [currentUser, refreshChatActivity]);

  const handlePopupConversationOpened = useCallback((conversationId: number) => {
    void markActivitiesRead(getConversationReactionActivityIds(chatActivities, conversationId));
  }, [markActivitiesRead, chatActivities]);

  const handleOpenReactionActivity = useCallback((activity: ChatActivity) => {
    // Opening a conversation clears all of its reaction alerts, not just the clicked one.
    handlePopupConversationOpened(activity.conversationId);
    setPopupChatTarget({
      conversationId: activity.conversationId,
      messageId: activity.messageId,
      threadRootMessageId: activity.threadRootMessageId ?? undefined,
    });
    selectedConversation$.next(activity.conversationId);
    currentConversationId$.next(activity.conversationId);
    setSelectedConversationId(activity.conversationId);
  }, [handlePopupConversationOpened]);

  const handleMarkAllReactionsRead = useCallback(() => {
    void markActivitiesRead(unreadReactionActivities.map(activity => activity.id));
  }, [markActivitiesRead, unreadReactionActivities]);


  // Universal Click-Outside Handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUsersDropdown && userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) setShowUsersDropdown(false);
      if (isProfileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) setIsProfileMenuOpen(false);
      if (isWaiverOpen && waiverRef.current && !waiverRef.current.contains(event.target as Node)) setIsWaiverOpen(false);
      if (isRegionOpen && regionRef.current && !regionRef.current.contains(event.target as Node)) setIsRegionOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUsersDropdown, isProfileMenuOpen, isWaiverOpen, isRegionOpen]);

  useEffect(() => {
    const match = location.pathname.match(/\/task\/([^/]+)/);
    setTaskId(match ? match[1] : "");
  }, [location]);

  const refreshConversations = useCallback(async () => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const data = await getMyConversations(token);

      console.debug("[CHAT-UNREAD] Conversation snapshot refreshed", {
        userId: currentUser.uid,
        conversationCount: data.length,
        unreadConversationIds: data
          .filter((conversation) => isConversationUnread(conversation, currentUser.uid))
          .map((conversation) => conversation.id),
      });

      setConversations(data);
    } catch (error) {
      console.error("[CHAT-UNREAD] Failed to refresh chat conversations:", {
        userId: currentUser.uid,
        error,
      });
    }
  }, [currentUser]);

  const profileFirstName = currentUserInfo?.firstName;
  const profileLastName = currentUserInfo?.lastName;
  const hasProfile = Boolean(currentUserInfo);
  const profileAvatar = currentAvatar?.avatar;

  useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  useEffect(() => {
const getTeam = async (token: string) => {
    if (currentUser && hasProfile) {
      const currentUserAsTeamMember: TeamUser = {
        id: currentUser.uid,
        firstName: profileFirstName || "",
        lastName: profileLastName || "",
        avatar: profileAvatar || "",
      };
      let teamData: TeamUser[] = [];
      if (currentRoles?.some((role) => role.name === "SUPERVISOR" || role.name === "PROGRAM MANAGER")) {
        const team = await getTeamBySupervisor(token);
        if (team) teamData = team;
      }
      const rawList = [currentUserAsTeamMember, ...teamData];
      const uniqueList = rawList.filter((user, index, self) => index === self.findIndex((u) => u.id === user.id));

      if (teamData && teamData.length > 0 && uniqueList.length > 1) {
        setUsers(uniqueList);
      } else {
        setUsers(undefined);
        selectedUserForTask$.next([currentUserAsTeamMember]);
      }
    }
  };

    if (!currentUser || !currentRoles) return;

    currentUser.getIdToken().then((token) => {
      if (token) getTeam(token);
    });
  }, [currentUser, currentRoles, hasProfile, profileFirstName, profileLastName, profileAvatar]);

  // Reconcile the header's unread state after the initial SignalR connection
  // and after every automatic reconnect. This recovers messages received while
  // the browser was offline or disconnected.
  useEffect(() => {
    if (connectionStatus !== "connected" || connectionVersion === 0) return;
    refreshConversations();
  }, [connectionStatus, connectionVersion, refreshConversations]);


  const handleMarkAsRead = useCallback(async (conversationId: number, messageId?: number) => {
    if (currentUser) {
      setConversations((prev) => prev.map((c) => c.id === conversationId ? { ...c, unreadCount: 0 } : c));
      if (messageId) {
        setChatActivities(previous => previous.map(activity =>
          activity.messageId === messageId ? { ...activity, read: true } : activity
        ));
        window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: { messageId } }));
      }
      const toastId = toastIdMap.current.get(conversationId);
      if (toastId) { toast.dismiss(toastId); toastIdMap.current.delete(conversationId); }

      const token = await currentUser.getIdToken();
      const response = await markConversationAsRead(token, conversationId);
      if (response.success) {
        if (messageId) {
          void markChatActivityForMessageRead(token, messageId).catch(error =>
            console.error("Unable to mark message activity as read", error)
          );
        }
      }
    }
  }, [currentUser]);



  useEffect(() => {
const handleReply = (message: Message) => {
    const threadRootMessageId = message.threadRootMessageId ?? message.parentMessageId;
    const toastId = toastIdMap.current.get(message.conversationId);
    if (toastId) {
      toast.dismiss(toastId);
      toastIdMap.current.delete(message.conversationId);
    }

    // Selecting the notification is itself a read action. Update both badge
    // sources immediately; the popup then performs the same durable read again
    // when its exact target is mounted, which is safe and idempotent.
    void handleMarkAsRead(message.conversationId, message.id);

    // The popup is intentionally not mounted on the full Chat route. If the
    // user is already there, target the same message in that view; everywhere
    // else the notification opens the preserved popup Chat.
    if (location.pathname.startsWith("/chat")) {
      const targetQuery = threadRootMessageId
        ? `?thread=${threadRootMessageId}&message=${message.id}`
        : `?message=${message.id}`;
      navigate(`/chat/${message.conversationId}${targetQuery}`);
      return;
    }

    setPopupChatTarget({
      conversationId: message.conversationId,
      messageId: message.id,
      threadRootMessageId,
    });
    selectedConversation$.next(message.conversationId);
    currentConversationId$.next(message.conversationId);
    setSelectedConversationId(message.conversationId);

    handleShowSideChat();
  };

    if (connection && currentUser) {
      const handleConversationState = (msg: Message) => {
        setConversations((prev) => {
          const exists = prev.some((conv) => conv.id === msg.conversationId);
          if (!exists) {
            void refreshConversations();
            return prev;
          }
          if (msg.parentMessageId !== null || msg.threadRootMessageId !== null) return prev;
          return prev.map((conv) => conv.id === msg.conversationId ? { ...conv, lastMessage: msg } : conv);
        });
      };

      const handleReceiveAttention = (msg: Message, reason: "message" | "mention" | "thread_reply" = "message") => {
        console.debug("[CHAT-UNREAD] SignalR message received in header", {
          activeUserId: currentUser.uid,
          authorId: msg.author?.id,
          conversationId: msg.conversationId,
          messageId: (msg as any).id,
        });

        if (msg.author?.id !== currentUser.uid) {
          const currentConversationId = currentConversationId$.getValue();
          const isSideChatOpen = showSideChatRef.current;
          const fullPageConversationId = Number(location.pathname.match(/^\/chat\/(\d+)/)?.[1]) || 0;
          const isViewingThisConversation =
            (isSideChatOpen && currentConversationId > 0 && currentConversationId === msg.conversationId) ||
            fullPageConversationId === msg.conversationId;

          if (!isViewingThisConversation) {
            const existingToastId = toastIdMap.current.get(msg.conversationId);
            const authorName =
              [msg.author?.firstName, msg.author?.lastName]
                .filter(Boolean)
                .join(" ") || "New message";
            const attachmentKind = msg.attachmentUrl
              ? msg.attachmentType?.startsWith("image")
                ? "image" as const
                : "file" as const
              : undefined;
            const rawPreview = attachmentKind === "image"
              ? "Sent an image"
              : attachmentKind === "file"
                ? "Sent an attachment"
                : msg.messageContent || "Sent a message";
            const messagePreview = reason === "mention"
              ? `Mentioned you: ${rawPreview}`
              : reason === "thread_reply"
                ? `Thread reply: ${rawPreview}`
                : rawPreview;
            const toastContent = (
              <ChatMessageToast
                authorId={msg.author?.id || ""}
                authorName={authorName}
                message={messagePreview}
                attachmentKind={attachmentKind}
                onMarkAsRead={() => handleMarkAsRead(msg.conversationId, msg.id)}
                onReply={() => handleReply(msg)}
              />
            );

            if (!existingToastId) {
              playNotificationSound("message_notification.mp3");
              const toastId = toast(
                toastContent, {
                toastId: `chat-message:${msg.conversationId}`,
                closeOnClick: false,
                closeButton: false,
                hideProgressBar: false,
                progressClassName: "waivera-chat-toast-progress",
                autoClose: 5000,
                className: "waivera-toast waivera-chat-toast",
                onClose: () => toastIdMap.current.delete(msg.conversationId),
              });
              toastIdMap.current.set(msg.conversationId, toastId);
            } else {
              toast.update(existingToastId, {
                render: toastContent,
                autoClose: 5000,
              });
            }
          }
          setConversations((prev) => {
            return prev.map((conv) =>
              conv.id === msg.conversationId
                  ? {
                    ...conv,
                    lastMessage: msg.parentMessageId !== null || msg.threadRootMessageId !== null
                      ? conv.lastMessage
                      : msg,
                    unreadCount: isViewingThisConversation
                      ? conv.unreadCount || 0
                      : (conv.unreadCount || 0) + 1,
                  }
                : conv,
            );
          });
        }
      };

      connection.on("ReceiveConversationListMessage", handleConversationState);
      connection.on("ReceiveChatAttention", handleReceiveAttention);
      connection.on("NewMessage", refreshConversations);

      return () => {
        connection.off("ReceiveConversationListMessage", handleConversationState);
        connection.off("ReceiveChatAttention", handleReceiveAttention);
        connection.off("NewMessage", refreshConversations);
      };
    }
  }, [currentUser, currentRoles, connection, location.pathname, refreshConversations, navigate, handleShowSideChat, handleMarkAsRead]);

  useEffect(() => {
const GetAllRegions = async () => {
    const token = await currentUser?.getIdToken();
    if (!token) return;
    getAllRegions(token, true).then((res) => { if (res.success) setRegion(res.data); }).catch((err) => toast.error(err.message));
  };

const GetWaivers = async () => {
    if (currentUser) {
      const token = await currentUser.getIdToken();
      const res = await getWaivers(token);
      if (res.success) setWaivers(res.data);
    }
  };

    if (currentUser && !currentRoles?.some((role) => role.name === "OWNER" || role.name === "ADMIN")) {
      const storedAvatars = getAvatarsFromStorage();
      if (storedAvatars) {
        const avatar = storedAvatars.find((a) => a.id === currentUser.uid);
        if (avatar) setCurrentAvatar(avatar);
      }
    }
    GetAllRegions();
    GetWaivers();
  }, [currentUser, currentRoles]);

  useEffect(() => {
    const subscription = selectedUserForTask$.subscribe(setSelectedUsers);
    return () => subscription.unsubscribe();
  }, []);

  const playNotificationSound = (filename: string | null = null) => {
    const soundFileName = filename || localStorage.getItem("notificationSound") || "notification-4.mp3";
    new Audio(`/notificationTones/${soundFileName}`).play().catch(console.error);
  };

  const getUserFormattedName = (firstName: string | undefined | null, lastName: string | undefined | null) => {
    const fName = firstName ? `${firstName.charAt(0).toUpperCase()}${firstName.substring(1)}` : "";
    const lName = lastName ? `${lastName.charAt(0).toUpperCase()}${lastName.substring(1)}` : "";
    return `${fName} ${lName}`.trim() || "";
  };





  const getHighestRole = (roles: string[]): string | null => {
    if (!roles || roles.length === 0) return null;
    const highestRawRole = roles.reduce((highest, role) => ROLE_PRIORITY[role] > ROLE_PRIORITY[highest] ? role : highest);
    return HEADER_ROLE_MAP[highestRawRole.toLowerCase().trim()] ?? highestRawRole;
  };



  const toggleUserSelection = (item: TeamUser) => {
    const isSelected = selectedUsers.some((user) => user.id === item.id);
    selectedUserForTask$.next(isSelected ? [] : [item]);
  };

  const handleShowChatHead = useCallback((show: boolean) => setShowChatHead(show), []);
  const handleToggleAddConversation = () => conversationListRef.current?.toggleAddConversation(addConversationButtonRef.current);

  const renderUserAvatar = (user: TeamUser, isSelected: boolean) => {
    const displayName = `${user.firstName} ${user.lastName}`.trim();

    return (
      <button
        key={user.id}
        type="button"
        aria-label={`${isSelected ? "Remove" : "Select"} ${displayName}`}
        aria-pressed={isSelected}
        className={`dashboard-user-selector-avatar relative w-8 h-8 md:w-9 md:h-9 rounded-lg flex flex-shrink-0 items-center justify-center border text-xs font-semibold -ml-2 cursor-pointer overflow-visible transition-all duration-200 hover:scale-110 hover:z-10 ${isSelected
          ? "bg-[#7E57C2] text-white border-[#7E57C2] scale-110 shadow-md z-10"
          : "bg-transparent border-gray-200 dark:border-white/10 text-gray-600 dark:text-zinc-300 hover:border-gray-300 dark:hover:border-white/20"
          }`}
        onClick={() => toggleUserSelection(user)}
      >
        <MilestoneAvatar
          userId={user.id}
          displayName={displayName}
          size="xxs"
          alternate
          tooltipContent={displayName}
          tooltipTheme={isDarkMode ? "dark" : "light"}
        />
      </button>
    );
  };

  const renderUserAvatars = () => {
    if (!users || (location.pathname !== "/home" && location.pathname !== "/auth2/dashboard")) return null;

    const visibleUsers = isMobile ? [] : users.slice(0, 5);
    const remainingUsers = isMobile ? users : users.slice(5);

    return (
      <div className="flex items-center pl-2 shrink-0">
        {visibleUsers.map((item) => renderUserAvatar(item, selectedUsers.some((user) => user.id === item.id)))}

        {remainingUsers.length > 0 && (
          <div className="relative flex items-center h-8 md:h-9 -ml-2" ref={userDropdownRef}>
            <div
              className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex flex-shrink-0 items-center justify-center border-2 text-xs font-semibold cursor-pointer transition-all duration-200 hover:scale-110 hover:z-10 ${showUsersDropdown
                ? "bg-[#7E57C2] text-white border-[#7E57C2] scale-110 shadow-md z-10"
                : "bg-white dark:bg-zinc-900 border-gray-200 dark:border-white/10 text-gray-600 dark:text-zinc-300 hover:border-gray-300 dark:hover:border-white/20"
                }`}
              onClick={() => setShowUsersDropdown(!showUsersDropdown)}
            >
              <Plus size={14} className={showUsersDropdown ? "text-white" : "text-slate-600 dark:text-zinc-300"} />
            </div>
            {showUsersDropdown && (
              <div className="absolute right-0 top-full mt-2 w-max rounded-xl bg-white dark:bg-[#171A24] shadow-lg dark:shadow-none border border-gray-100 dark:border-white/[.07] z-20 py-1">
                {remainingUsers.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[.045] transition-colors ${selectedUsers.some((u) => u.id === item.id) ? "bg-purple-50 dark:bg-[#7E57C2]/15 text-[#7E57C2] dark:text-purple-300" : "text-gray-700 dark:text-zinc-300"}`}
                    onClick={() => { toggleUserSelection(item); setShowUsersDropdown(false); }}
                  >
                    <div className="relative mr-2 h-6 w-6 shrink-0 overflow-visible rounded-lg">
                      <MilestoneAvatar
                        userId={item.id}
                        displayName={`${item.firstName} ${item.lastName}`}
                        size="xxxs"
                        alternate
                        tooltipContent={`${item.firstName} ${item.lastName}`}
                        tooltipTheme={isDarkMode ? "dark" : "light"}
                      />
                    </div>
                    <span className="text-sm font-medium">{item.firstName} {item.lastName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

    // const allowChat = hasFeatureAccess(userAddedInRolePolicy, subscriptionDetails, PlanFeatureKey.CHAT);
    const hasAccessOfBilling = hasPolicyPermissionV2(
        currentOrganizationRolePolicies,
        POLICYPERMISSIONS.BillingPolicies.View.key,
        userAddedInRolePolicy
    );
    const hasFormSettingsAccess = hasPolicyPermissionV2(
        currentOrganizationRolePolicies,
        POLICYPERMISSIONS.FormPolicies.Manage.key,
        userAddedInRolePolicy,
        POLICYPERMISSIONS.FormPolicies.Manage.scope.Organization,
        false,
    );
    const hasDashboardSettingsAccess = hasPolicyPermissionV2(
        currentOrganizationRolePolicies,
        POLICYPERMISSIONS.DashboardWidgetPolicies.Manage.key,
        userAddedInRolePolicy,
        POLICYPERMISSIONS.DashboardWidgetPolicies.Manage.scope.Organization,
        false,
    );
    const hasChannelSettingsAccess = hasPolicyPermissionV2(
        currentOrganizationRolePolicies,
        POLICYPERMISSIONS.ChannelPolicies.AdminView.key,
        userAddedInRolePolicy,
        undefined,
        false,
    );
    const canAccessSettings = Boolean(
        isOwner ||
        isSuperAdmin ||
        hasFormSettingsAccess ||
        hasDashboardSettingsAccess ||
        hasChannelSettingsAccess
    );
    


  return (
    <div className="app-header-shell">
      {/* 2. Place the banner ABOVE the header, not inside it */}
      {impersonatedUserId && (
        <div className="bg-slate-900 text-slate-200 px-4 py-2 flex flex-col sm:flex-row items-center justify-between gap-3 w-full border-b border-[#7E57C2]/30 shadow-inner">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-6 h-6 rounded bg-[#7E57C2]/20 text-[#7E57C2] shrink-0">
              <Users size={14} />
            </div>
            <span className="text-sm font-medium tracking-wide m-0">
              Operating as <code className="font-mono text-[#7E57C2] bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700 ml-1">{impersonatedUserId}</code>
            </span>
          </div>
          
          <button
            onClick={async () => {
              await setImpersonation(null);
              window.location.reload();
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-md text-xs font-semibold transition-colors border border-slate-600 shrink-0 cursor-pointer shadow-sm"
          >
            <LogOut size={14} />
            Return to Admin
          </button>
        </div>
      )}
      <header className="app-header app-header--responsive dark-surface bg-[#F9F6FF] dark:bg-zinc-900 dark:border-b dark:border-white/5 px-4 py-3 md:py-3 flex items-center justify-between gap-3 md:gap-4 min-w-0 transition-colors w-full">
        {/* Left Side: Search & Navigation Info */}
        <div className="app-header-left flex items-center gap-3 md:gap-4 flex-1 min-w-0">

          {/* Mobile Sidebar Toggle */}
          <div
            className="cursor-pointer flex items-center justify-center p-1 -ml-1 text-slate-600 dark:text-slate-300 hover:text-[#7E57C2] transition-colors xl:!hidden shrink-0"
            onClick={handleShowSidebar}
          >
            <Menu size={20} strokeWidth={2.5} />
          </div>

          {/* Page Title */}
          <div className="hidden md:block truncate shrink-0 max-w-[200px] lg:max-w-xs">
            <h1 className="app-header-page-title text-lg md:text-xl font-semibold text-gray-900 dark:text-slate-100 m-0 truncate">
              {metadataTitle || getPageTitle(location.pathname)}
            </h1>
          </div>

          {/* Owner Dashboards Dropdowns */}
          {isOwner && (location.pathname === "/dashboard" || location.pathname.endsWith("/dashboard/owner")) && (
            <>
              {/* Waiver Dropdown */}
              <div className="relative hidden md:block shrink-0" ref={waiverRef}>
                <button
                  onClick={() => setIsWaiverOpen(!isWaiverOpen)}
                  className="app-header-selector dark-surface-muted dark-borderless flex items-center justify-between gap-2 px-3 py-1.5 bg-white dark:bg-zinc-800 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors shadow-sm dark:shadow-none focus:outline-none min-w-[130px]"
                >
                  <span className="truncate">{selectedWaiver ? selectedWaiver.waiverName : "Waiver"}</span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${isWaiverOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`dark-popup absolute top-[110%] left-0 w-56 bg-white dark:bg-zinc-800 border border-gray-100 rounded-xl shadow-[0_4px_20px_rgb(0,0,0,0.1)] z-50 transition-all duration-200 origin-top-left ${isWaiverOpen ? 'opacity-100 scale-100 visible translate-y-0' : 'opacity-0 scale-95 invisible -translate-y-2'}`}>
                  <div className="py-1.5 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
                    {waivers.map((w) => (
                      <button
                        key={w.id}
                        onClick={() => { setSelectedWaiver(w); UpdateSelectedWaiverIdFromHeaderSubject(w.id); setIsWaiverOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedWaiver?.id === w.id ? 'text-[#7E57C2] dark:text-purple-300 font-semibold bg-purple-50/50 dark:bg-purple-400/10' : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                      >
                        {w.waiverName}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Region Dropdown */}
              <div className="relative hidden md:block shrink-0" ref={regionRef}>
                <button
                  onClick={() => setIsRegionOpen(!isRegionOpen)}
                  className="app-header-selector dark-surface-muted dark-borderless flex items-center justify-between gap-2 px-3 py-1.5 bg-white dark:bg-zinc-800 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors shadow-sm dark:shadow-none focus:outline-none min-w-[130px]"
                >
                  <span className="truncate">{selectedRegion?.regionName || "Region"}</span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${isRegionOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`dark-popup absolute top-[110%] left-0 w-56 bg-white dark:bg-zinc-800 border border-gray-100 rounded-xl shadow-[0_4px_20px_rgb(0,0,0,0.1)] z-50 transition-all duration-200 origin-top-left ${isRegionOpen ? 'opacity-100 scale-100 visible translate-y-0' : 'opacity-0 scale-95 invisible -translate-y-2'}`}>
                  <div className="py-1.5 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
                    {region.map((r) => (
                      <button
                        key={r.regionId}
                        onClick={() => { setSelectedRegion(r); UpdateSelectedRegionIdFromHeaderSubject(r.regionId); setIsRegionOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${r.regionId === selectedRegion?.regionId ? 'text-[#7E57C2] dark:text-purple-300 font-semibold bg-purple-50/50 dark:bg-purple-400/10' : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                      >
                        {r.regionName}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Mobile Global Search */}
          <div className="block md:hidden flex-1 min-w-0">
            <GlobalSearch />
          </div>

          {renderUserAvatars()}
        </div>

        {/* Right Side: Actions & Icons */}
        <div className="app-header-right flex items-center gap-3 md:gap-5 shrink-0">
          <ul className="app-header-actions flex items-center justify-end gap-3 md:gap-4 m-0 p-0 list-none shrink-0" ref={headerRightIconRef}>

            {/* Desktop Global Search */}
            <li className="app-header-search hidden md:block w-48 lg:w-64">
              <GlobalSearch />
            </li>

            {!isSuperAdmin && <li className={`hidden md:block ${showOverflowMenu ? "hidden" : ""}`}>
              <AddNewBtn />
            </li>}

            <li className={`hidden h-8 w-8 shrink-0 items-center justify-center md:flex ${showOverflowMenu ? "hidden" : ""}`}>
              <HeaderCalendarDropdown />
            </li>

            {/* Notification Bell */}
            <li className={`shrink-0 hidden md:flex items-center ${showOverflowMenu ? "hidden" : ""}`}>
              <div
                className={`app-header-icon-button relative flex items-center justify-center w-8 h-8 rounded-full cursor-pointer transition-colors ${showNotificationPanel ? "bg-purple-50 dark:bg-purple-400/10 text-[#7E57C2] dark:text-purple-300" : "text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-[#7E57C2]"}`}
                onClick={() => setShowNotificationPanel(true)}
              >
                <Bell size={18} strokeWidth={2} className={`${hasUnread ? "animate-bell-burst origin-[50%_10%]" : ""}`} />
                {hasUnread && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />}
              </div>
            </li>

            {/* Chat Icon: the slot remains mounted on every route so the header never jumps. */}
            <li className={`hidden h-8 w-8 shrink-0 items-center justify-center md:flex ${showOverflowMenu ? "hidden" : ""}`}>
              <Tooltip
                content={location.pathname.startsWith("/chat")
                  ? new URLSearchParams(location.search).get("view") ? "Back to messages" : "Focus messages"
                  : showSideChat ? "Hide messages" : "Open messages"}
                position="bottom"
                openDelay={350}
              >
                <button
                  type="button"
                  aria-label={location.pathname.startsWith("/chat") ? "Focus messages" : showSideChat ? "Hide messages" : "Open messages"}
                  aria-pressed={!location.pathname.startsWith("/chat") && showSideChat}
                  onClick={handleChatIconClick}
                  className={`app-header-icon-button chat-header-icon relative flex h-8 w-8 items-center justify-center rounded-full border-0 p-0 transition-colors ${
                    location.pathname.startsWith("/chat") || showSideChat
                      ? "bg-purple-50 text-[#7E57C2] dark:bg-purple-400/10 dark:text-purple-300"
                      : "bg-transparent text-gray-600 hover:bg-gray-50 hover:text-[#7E57C2] dark:text-slate-300 dark:hover:bg-slate-800"
                  } ${chatIconFeedback ? "chat-header-icon--feedback" : ""}`}
                >
                  <LucideMessageCircleMore size={18} strokeWidth={2} />
                  {(hasUnreadMessages || chatActivities.some(activity => !activity.read)) && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />}
                </button>
              </Tooltip>
            </li>
             
           
          </ul>

          <NotificationPanel isOpen={showNotificationPanel} onClose={() => setShowNotificationPanel(false)} notifications={notifications} />

          <MobileMenu
            hasUnread={hasUnread}
            hasUnreadMessages={hasUnreadMessages || chatActivities.some(activity => !activity.read)}
            hasUnreadActivity={chatActivities.some(activity => !activity.read)}
            onOpenRecentActivity={() => {
              navigate("/chat?view=activity");
            }}
            showOnDesktop={showOverflowMenu}
            includeAccountActions={!showOverflowMenu}
            currentUserId={currentUser?.uid}
            userName={getUserFormattedName(
              currentUserInfo?.firstName,
              currentUserInfo?.lastName
            )}
            roleName={
              impersonatedUserId
                ? "IMPERSONATING"
                : currentOrganizationRoles && currentOrganizationRoles.length > 0
                  ? currentOrganizationRoles[0].name
                  : currentUserInfo?.roles && currentUserInfo.roles.length > 0
                    ? getHighestRole(currentUserInfo.roles.map((r) => r.name)) ?? undefined
                    : undefined
            }
            showBilling={isOwner || hasAccessOfBilling}
            showSettings={canAccessSettings}
            isSuperAdmin={!!isSuperAdmin}
            impersonatedUserId={impersonatedUserId}
            onImpersonate={async (userId) => {
              await setImpersonation(userId);
              window.location.reload();
            }}
            onSignOut={() => navigate("/logout")}
            canUseDarkMode={canUseDarkMode}
            isDarkMode={isDarkMode}
            onToggleDarkMode={onToggleDarkMode}
          />

          {/* Persona Switcher (Sandbox) */}
          {/* <div className="md:block">
            <PersonaSwitcher />
          </div> */}

          {/* Profile Menu */}
          <div className="relative z-50 ml-1 hidden md:block" ref={profileMenuRef}>
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center gap-3 bg-transparent border-none cursor-pointer focus:outline-none transition-transform duration-200 active:scale-95 text-left p-0"
            >
              <div className="flex items-center gap-2">
                <div className="text-right hidden lg:block">
                  <h3 className={`text-sm font-semibold mb-0 leading-tight transition-colors ${impersonatedUserId ? 'text-red-600' : 'text-gray-900 dark:text-slate-100'}`}>
                    {getUserFormattedName(currentUserInfo?.firstName, currentUserInfo?.lastName)}
                  </h3>
                  <p className={`text-[10px] mb-0 font-bold uppercase tracking-wider transition-colors ${impersonatedUserId ? 'text-red-500 animate-pulse' : 'text-gray-500 dark:text-slate-400'}`}>
                    {impersonatedUserId ? 'IMPERSONATING' : (
                      currentOrganizationRoles && currentOrganizationRoles.length > 0
                      ? currentOrganizationRoles[0].name
                      : currentUserInfo?.roles && currentUserInfo?.roles?.length > 0
                        ? getHighestRole(currentUserInfo.roles.map((r) => r.name))
                        : "N/A"
                    )}
                  </p>
                </div>
                <figure className="m-0 relative shrink-0">
                  <div
                    className={`rounded-xl p-0.5 ring-2 transition-all duration-200 ${
                      isProfileMenuOpen
                        ? "bg-[#7E57C2]/10 ring-[#7E57C2]/40 shadow-sm"
                        : "bg-transparent ring-transparent hover:bg-white hover:ring-gray-200 hover:shadow-sm"
                    }`}
                  >
                    {currentUser && (
                      <MilestoneAvatar
                        userId={currentUser.uid}
                        displayName={getUserFormattedName(
                          currentUserInfo?.firstName,
                          currentUserInfo?.lastName
                        )}
                        size="xs"
                        alternate
                        labelMode="none"
                      />
                    )}
                  </div>
                </figure>
              </div>
            </button>

            <div className={`dark-popup absolute right-0 top-[125%] w-56 origin-top-right rounded-2xl bg-white dark:bg-zinc-900 shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.45)] border border-gray-100 p-2 transition-all duration-400 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)] ${isProfileMenuOpen ? "scale-100 opacity-100 translate-y-0 visible" : "scale-50 opacity-0 -translate-y-6 invisible"}`}>
              <div className="flex flex-col gap-1">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-3 py-2.5 text-[12px] font-medium text-gray-700 dark:text-slate-200 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-[#7E57C2] transition-colors no-underline group"
                  onClick={() => setIsProfileMenuOpen(false)}
                >
                  <User size={16} className="text-gray-400 group-hover:text-[#7E57C2] transition-colors" />
                  My Profile
                </Link>
                {/* {canUseDarkMode && ( */}
                  <button
                    type="button"
                    onClick={onToggleDarkMode}
                    className="flex items-center gap-3 w-full px-3 py-2.5 text-left text-[12px] font-medium text-gray-700 dark:text-slate-200 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-[#7E57C2] dark:hover:text-purple-300 transition-colors border-none bg-transparent group"
                    aria-pressed={isDarkMode}
                  >
                    {isDarkMode ? (
                      <Sun size={16} className="text-gray-400 dark:text-slate-400 group-hover:text-[#7E57C2]" />
                    ) : (
                      <Moon size={16} className="text-gray-400 group-hover:text-[#7E57C2]" />
                    )}
                    {isDarkMode ? "Use light mode" : "Use dark mode"}
                  </button>
                {/* )} */}
                {
                    (isOwner || hasAccessOfBilling) && (
                        <Link
                            to="/dashboard/owner/billing-details"
                            className="flex items-center gap-3 px-3 py-2.5 text-[12px] font-medium text-gray-700 dark:text-slate-200 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-[#7E57C2] transition-colors no-underline group"
                            onClick={() => setIsProfileMenuOpen(false)}
                        >
                            <CreditCard size={16} className="text-gray-400 group-hover:text-[#7E57C2] transition-colors" />
                            Billing
                        </Link>
                    )
                }

              <Link
                  to="/faqs"
                  className="flex items-center gap-3 px-3 py-2.5 text-[12px] font-medium text-gray-700 dark:text-slate-200 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-[#7E57C2] transition-colors no-underline group"
                  onClick={() => setIsProfileMenuOpen(false)}
              >
                  <BadgeQuestionMark size={16} className="text-gray-400 group-hover:text-[#7E57C2] transition-colors" />
                  FAQs
              </Link>
                {canAccessSettings && (
                  <>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-3 py-2.5 text-[12px] font-medium text-gray-700 dark:text-slate-200 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-[#7E57C2] transition-colors no-underline group"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <Settings size={16} className="text-gray-400 group-hover:text-[#7E57C2] transition-colors" />
                      Settings
                    </Link>
                  </>
                )}


                {/* --- SUPER ADMIN IMPERSONATION BLOCK --- */}
                {isSuperAdmin && !impersonatedUserId && (
                  <>
                    <div className="h-px bg-gray-100 dark:bg-slate-800 my-1 mx-2" />
                    <div className="dark-border-subtle px-3 py-2 bg-purple-50/50 dark:bg-purple-400/10 rounded-xl mx-1 border border-purple-100/50">
                      <p className="text-[10px] font-bold text-[#7E57C2] mb-2 uppercase tracking-wider">Super Admin Tools</p>
                      <form 
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!targetUserId.trim()) return;
                          await setImpersonation(targetUserId.trim());
                          window.location.reload(); // Force hard reload to clear caches
                        }} 
                        className="flex flex-col gap-2"
                      >
                        <input
                          type="text"
                          value={targetUserId}
                          onChange={(e) => setTargetUserId(e.target.value)}
                          onClick={(e) => e.stopPropagation()} // Keep dropdown open when typing
                          placeholder="Target User ID"
                          className="dark-surface-muted dark-input-borderless w-full px-2 py-1.5 text-[11px] border border-purple-200 rounded-md focus:outline-none bg-white dark:bg-zinc-800 text-gray-800 dark:text-zinc-100"
                        />
                        <button
                          type="submit"
                          disabled={!targetUserId.trim()}
                          className="w-full py-1.5 bg-[#7E57C2] text-white rounded-md text-[11px] font-semibold hover:bg-[#6a48a3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Assume Role
                        </button>
                      </form>
                    </div>
                  </>
                )}
                <div className="h-px bg-gray-100 dark:bg-slate-800 my-1 mx-2" />
                <button
                  className="flex items-center gap-3 w-full text-left px-3 py-2.5 text-[12px] font-medium text-red-600 dark:text-red-400 rounded-xl hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors border-none cursor-pointer group bg-transparent"
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    navigate("/logout")
                  }}
                >
                  <LogOut size={16} className="text-red-400 group-hover:text-red-600 transition-colors" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {!location.pathname.startsWith('/chat') && <>
      {/* Pop-out Chat Panel Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] transition-opacity duration-300 md:hidden ${showSideChat ? 'visible opacity-100 bg-black/20' : 'invisible opacity-0'}`}
        onClick={handleCloseSideChat}
      />

      {/* Pop-out Chat Panel */}
      <div
        role="dialog" aria-label="Messages"
        className={`dark-popup fixed bottom-4 sm:bottom-6 lg:bottom-8 right-4 sm:right-6 lg:right-8 w-[calc(100%-32px)] sm:w-[420px] h-[calc(100vh-100px)] sm:h-[650px] max-h-[calc(100vh-100px)] bg-white dark:bg-slate-900 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.16)] border border-gray-200 dark:border-transparent flex flex-col z-[9999] transition-all duration-300 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)] origin-bottom-right overflow-hidden ${showSideChat ? 'scale-100 opacity-100 translate-y-0 visible' : 'scale-90 opacity-0 translate-y-8 invisible pointer-events-none'}`}
      >
        {showChatHead && (
          <div className="dark-surface shrink-0 flex h-12 items-center justify-between bg-white dark:bg-slate-900 px-3 z-10">
            <div className="min-w-0">
              <h2 className="m-0 truncate text-[15px] font-semibold text-slate-900 dark:text-slate-100">Messages</h2>
            </div>
            <div className="flex items-center gap-1">
              <Tooltip content={"Open chat page"}>
                <button
                  type="button"
                  onClick={() => {
                    handleCloseSideChat();
                    navigate(getPopupExpandTarget(chatActivities, selectedConversationId));
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border-none bg-slate-100 p-0 text-slate-500 transition-colors hover:bg-purple-50 hover:text-[#7E57C2] dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-purple-300"
                  aria-label="Open chat page"
                >
                  <Maximize2 size={17} strokeWidth={2} />
                </button>
              </Tooltip>
              <Tooltip content={"Minimize chat"}>
                <button
                  type="button"
                  onClick={handleCloseSideChat}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border-none bg-slate-100 p-0 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
                  aria-label="Minimize chat"
                >
                  <Minus size={18} strokeWidth={2} />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
        <div className="dark-surface relative flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-slate-900">
          <ChatPage
              ref={conversationListRef}
              isSideView={true}
              resourceComposeRequest={resourceComposeRequest}
              onResourceComposeFinished={() => { setResourceComposeRequest(null); resourceComposeRequest$.next(null); }}
              isVisible={showSideChat}
              handleShowChatHead={handleShowChatHead}
              showHeader={false}
              handleCloseChat={handleCloseChat}
              hasUnreadActivity={chatActivities.some(activity => !activity.read)}
              linkedThreadRootId={popupChatTarget?.conversationId === selectedConversationId ? popupChatTarget.threadRootMessageId : undefined}
              linkedMessageId={popupChatTarget?.conversationId === selectedConversationId ? popupChatTarget.messageId : undefined}
              onClearLinkedTarget={() => setPopupChatTarget(null)}
              reactionActivitySlot={
                <PopupReactionActivity
                  activities={unreadReactionActivities}
                  onOpenActivity={handleOpenReactionActivity}
                  onMarkAllRead={handleMarkAllReactionsRead}
                />
              }
              reactionBadges={reactionBadges}
              onConversationOpened={handlePopupConversationOpened}
              handleToggleAddConversation={handleToggleAddConversation}
              addConversationButtonRef={addConversationButtonRef}
            />
        </div>
      </div>
      </>}
      <BillingAlertBanner />
    </div>
  );
}
